import { useState, useEffect, useEffectEvent } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import DialogContentText from '@mui/material/DialogContentText';
import { useNavigate } from 'react-router';

import {
    IModuleType,
    DeleteModuleDocument,
    ModuleEditDocument,
    UpdateCertificateModuleDocument,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { useHashMatch } from 'route/hooks/useHashMatch';
import { PageTitle } from 'common/components/PageTitle';
import { IModuleUpdateDrawerProps } from 'training/types';
import { Loader } from 'common/components/Loader';
import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { getImageId } from 'common/utils/image';
import { getUrl } from 'route/utils/getUrl';
import { CertificateModuleForm } from 'training/components/forms';

import { ICertificateModuleFormProps } from '../forms/CertificateModuleForm';

import { CertificateModuleSelectDrawer } from './CertificateModuleSelectDrawer';

export const CertificateModuleUpdateDrawer = ({
    id,
    onClose,
}: IModuleUpdateDrawerProps) => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();

    // The certificate ID comes from the server-loaded module; replacing it is
    // driven by the select drawer's onSelect below. Unlike the create flow,
    // there is no `?certificate=` URL param to consume here.
    const [selectedCertificateId, setSelectedCertificateId] =
        useState<string>();

    const certificateSelectDrawerOpen = useHashMatch('TRAINING_MODULE_SELECT');

    const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);

    const { data, loading, error } = useQuery(ModuleEditDocument, {
        variables: { id, type: IModuleType.Certificate },
    });

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const [updateModule] = useMutation(UpdateCertificateModuleDocument);
    const [deleteModule, { loading: loadingDelete }] =
        useMutation(DeleteModuleDocument);

    const module = data?.module;

    if (loading || !module) return <Loader />;

    if (!module || module.__typename !== 'CertificateModule') return null;

    const handleSubmit: ICertificateModuleFormProps['onSubmit'] = async (
        values
    ) => {
        const { image, ...otherValues } = values;
        const { image: currentImage } = module;
        const moduleValues = {
            ...otherValues,
            imageId: getImageId(currentImage, image),
        };

        try {
            await updateModule({
                variables: { id, module: moduleValues },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.certificate.update'), {
            variant: 'success',
        });

        onClose?.();

        return;
    };

    const handleDelete = async () => {
        try {
            await deleteModule({
                variables: {
                    id,
                    type: IModuleType.Certificate,
                },
                update: (cache) => {
                    cache.evict({ id: `CertificateModule:${id}` });
                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.certificate.delete'), {
            variant: 'success',
        });

        onClose?.();

        return;
    };

    const {
        certificate,
        title,
        description,
        titleOverride,
        image,
        moduleGroup: { id: moduleGroupId },
    } = module;

    const initialValues = {
        title,
        description,
        titleOverride: titleOverride || '',
        certificateId: certificate?.id || '',
        image: image || undefined,
    };

    return (
        <>
            <PageTitle>{translate('updateCertificateModule')}</PageTitle>

            <CertificateModuleSelectDrawer
                edit
                open={certificateSelectDrawerOpen}
                onClose={() => {
                    navigate(window.location.pathname);
                }}
                onSelect={(picked) => {
                    setSelectedCertificateId(picked.id);
                    navigate(window.location.pathname);
                }}
            />

            <CertificateModuleForm
                edit
                certificate={certificate || undefined}
                certificateId={selectedCertificateId}
                initialValues={initialValues}
                moduleGroupId={moduleGroupId}
                onClose={onClose}
                onDelete={() => setShowDeleteAlert(true)}
                onReplaceCertificate={() => {
                    navigate(getUrl('TRAINING_MODULE_SELECT'));
                }}
                onSubmit={handleSubmit}
            />

            <AlertDialog
                actions={
                    <>
                        <Button
                            autoFocus
                            color="error"
                            loading={loadingDelete}
                            variant="contained"
                            onClick={handleDelete}
                        >
                            {translate('delete')}
                        </Button>
                        <Button
                            disabled={loadingDelete}
                            variant="contained"
                            onClick={() => {
                                setShowDeleteAlert(false);
                            }}
                        >
                            {translate('cancel')}
                        </Button>
                    </>
                }
                open={showDeleteAlert}
                title={translate('deleteCertificateModuleMessage.title')}
            >
                <DialogContentText color="text.primary" variant="body2">
                    {translate('deleteCertificateModuleMessage.text')}
                </DialogContentText>
            </AlertDialog>
        </>
    );
};
